// 네이티브 STT (@capacitor-community/speech-recognition).
// 웹(브라우저)은 Vent의 Web Speech API를 그대로 쓰고, 네이티브 WebView에선 이 플러그인을 쓴다
// (Android/iOS WebView는 webkitSpeechRecognition이 껍데기라 인식·종료가 안 됨).
import { Capacitor, type PluginListenerHandle } from "@capacitor/core";
import { SpeechRecognition } from "@capacitor-community/speech-recognition";

export function isNativeSTT(): boolean {
  return Capacitor.isNativePlatform();
}

let handles: PluginListenerHandle[] = [];
function clearHandles() {
  handles.forEach((h) => h.remove());
  handles = [];
}

/**
 * 네이티브 음성 인식 시작.
 * @param onPartial 현재까지 인식된 "전체 문구"(증분 아님 — 매번 최신 문구로 교체).
 * @param onEnd 인식이 멈췄을 때(침묵 타임아웃·stop 호출).
 * @returns 시작 성공 여부(미지원·권한 거부면 false).
 */
export async function startNativeSTT(
  language: string,
  onPartial: (full: string) => void,
  onEnd: () => void,
): Promise<boolean> {
  try {
    const { available } = await SpeechRecognition.available();
    if (!available) return false;

    let perm = await SpeechRecognition.checkPermissions();
    if (perm.speechRecognition !== "granted") {
      perm = await SpeechRecognition.requestPermissions();
      if (perm.speechRecognition !== "granted") return false;
    }

    clearHandles();
    handles.push(
      await SpeechRecognition.addListener("partialResults", (data) => {
        const m = data.matches?.[0];
        if (m) onPartial(m);
      }),
    );
    handles.push(
      await SpeechRecognition.addListener("listeningState", (data) => {
        if (data.status === "stopped") {
          clearHandles();
          onEnd();
        }
      }),
    );

    await SpeechRecognition.start({ language, partialResults: true, popup: false, maxResults: 1 });
    return true;
  } catch {
    clearHandles();
    return false;
  }
}

/** 인식 중지. listeningState 'stopped'가 와서 onEnd도 호출된다. */
export async function stopNativeSTT(): Promise<void> {
  try {
    await SpeechRecognition.stop();
  } catch {
    /* 이미 멈췄을 수 있음 */
  }
  clearHandles();
}
